<template>
  <ContentWrap title="日历库存管理">
    <el-alert class="mb-16px" type="info" :closable="false" title="价格与库存可在“日历量价合并”页面统一维护，本页面继续保留。" />
    <!-- 搜索工作栏 -->
    <el-form :model="queryParams" ref="queryFormRef" :inline="true" label-width="68px" class="-mb-15px nowrap-form" style="display: flex; flex-wrap: nowrap; align-items: center;">
      <el-form-item label="店铺" prop="shopId">
        <el-select
          v-model="queryParams.shopId"
          placeholder="请选择店铺"
          clearable
          class="!w-140px"
        >
          <el-option
            v-for="shop in shopList"
            :key="shop.id"
            :label="shop.name"
            :value="shop.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="房型" prop="spuId">
        <el-select
          v-model="queryParams.spuId"
          placeholder="请选择房型"
          clearable
          class="!w-140px"
        >
          <el-option
            v-for="spu in spuList"
            :key="spu.id"
            :label="spu.name"
            :value="spu.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="日期范围" prop="dateRange">
        <el-date-picker
          v-model="queryParams.dateRange"
          type="daterange"
          value-format="YYYY-MM-DD"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          class="!w-200px"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" /> 搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" /> 重置</el-button>
        <el-button
          type="primary"
          plain
          @click="openBatchSetDialog"
          v-hasPermi="['hotel:inventory:update']"
        >
          <Icon icon="ep:setting" class="mr-5px" /> 批量设置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 库存列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true" border>
      <el-table-column label="店铺" align="center" width="120">
        <template #default="scope">
          <span>{{ getShopName(scope.row.shopId) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="房型" align="center" width="120">
        <template #default="scope">
          <span>{{ getSpuName(scope.row.spuId) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="日期" align="center" prop="date" width="120" />
      <el-table-column label="总库存" align="center" prop="totalCount" width="100">
        <template #default="scope">
          <el-tag type="primary">{{ scope.row.totalCount }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="锁定库存" align="center" prop="lockedCount" width="100">
        <template #default="scope">
          <el-tag type="warning">{{ scope.row.lockedCount }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="已售库存" align="center" prop="soldCount" width="100">
        <template #default="scope">
          <el-tag type="danger">{{ scope.row.soldCount }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="可用库存" align="center" prop="availableCount" width="100">
        <template #default="scope">
          <el-tag :type="scope.row.availableCount > 0 ? 'success' : 'danger'">
            {{ scope.row.availableCount }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <!-- 批量设置弹窗 -->
  <Dialog :title="'批量设置库存'" v-model="batchDialogVisible" width="500px">
    <el-form ref="batchFormRef" :model="batchFormData" :rules="batchFormRules" label-width="100px" v-loading="batchFormLoading">
      <el-form-item label="店铺" prop="shopId">
        <el-select
          v-model="batchFormData.shopId"
          placeholder="请选择店铺"
          clearable
          filterable
        >
          <el-option
            v-for="shop in shopList"
            :key="shop.id"
            :label="shop.name"
            :value="shop.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="房型" prop="spuId">
        <el-select
          v-model="batchFormData.spuId"
          placeholder="请选择房型"
          clearable
          filterable
        >
          <el-option
            v-for="spu in spuList"
            :key="spu.id"
            :label="spu.name"
            :value="spu.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="日期范围" prop="dateRange">
        <el-date-picker
          v-model="batchFormData.dateRange"
          type="daterange"
          value-format="YYYY-MM-DD"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          class="!w-1/1"
        />
      </el-form-item>
      <el-form-item label="总库存" prop="totalCount">
        <el-input-number v-model="batchFormData.totalCount" :min="0" :precision="0" class="!w-1/1" placeholder="请输入总库存" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitBatchForm" type="primary" :disabled="batchFormLoading">确 定</el-button>
      <el-button @click="batchDialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as inventoryApi from '@/api/hotel/inventory'
import { getShopSimpleList } from '@/api/hotel/platformShop'
import { getSpuSimpleList } from '@/api/mall/product/spu'

defineOptions({ name: 'HotelInventory' })

const message = useMessage()

const loading = ref(true)
const shopList = ref<{ id: string | number; name: string }[]>([])
const spuList = ref<{ id: string | number; name: string }[]>([])

const fetchDropdownData = async () => {
  try {
    const [shopRes, spuRes] = await Promise.all([
      getShopSimpleList(),
      getSpuSimpleList()
    ])
    shopList.value = shopRes || []
    spuList.value = spuRes || []
  } catch {
    // 静默处理
  }
}
const list = ref<inventoryApi.InventoryVO[]>([])
const total = ref(0)
const shopNameMap = computed(() => {
  const map = new Map<string | number, string>()
  shopList.value.forEach((item) => map.set(item.id, item.name))
  return map
})
const spuNameMap = computed(() => {
  const map = new Map<string | number, string>()
  spuList.value.forEach((item) => map.set(item.id, item.name))
  return map
})
const getShopName = (id: string | number) => shopNameMap.value.get(id) || '-'
const getSpuName = (id: string | number) => spuNameMap.value.get(id) || '-'
const queryParams = reactive({
  shopId: undefined,
  spuId: undefined,
  dateRange: [] as string[]
})
const queryFormRef = ref()

const getList = async () => {
  loading.value = true
  try {
    const params: any = {}
    if (queryParams.shopId) params.shopId = queryParams.shopId
    if (queryParams.spuId) params.spuId = queryParams.spuId
    if (queryParams.dateRange && queryParams.dateRange.length === 2) {
      params.startDate = queryParams.dateRange[0]
      params.endDate = queryParams.dateRange[1]
    }
    const data = await inventoryApi.queryCalendar(params)
    // 后端返回 List（非分页），直接赋值
    list.value = Array.isArray(data) ? data : []
    total.value = list.value.length
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  queryParams.dateRange = []
  handleQuery()
}

// 批量设置
const batchDialogVisible = ref(false)
const batchFormLoading = ref(false)
const batchFormRef = ref()
const batchFormData = reactive({
  shopId: undefined as string | number | undefined,
  spuId: undefined as string | number | undefined,
  dateRange: [] as string[],
  totalCount: 0
})
const batchFormRules = reactive({
  shopId: [{ required: true, message: '请选择店铺', trigger: 'change' }],
  spuId: [{ required: true, message: '请选择房型', trigger: 'change' }],
  dateRange: [{ required: true, message: '请选择日期范围', trigger: 'change' }],
  totalCount: [{ required: true, message: '请输入总库存', trigger: 'blur' }]
})

const openBatchSetDialog = () => {
  batchFormData.shopId = undefined
  batchFormData.spuId = undefined
  batchFormData.dateRange = []
  batchFormData.totalCount = 0
  batchFormRef.value?.resetFields()
  batchDialogVisible.value = true
}

const submitBatchForm = async () => {
  await batchFormRef.value.validate()
  batchFormLoading.value = true
  try {
    const [startDate, endDate] = batchFormData.dateRange
    // 展开日期范围，每条记录一个日期
    const records = []
    let cur = new Date(startDate)
    const end = new Date(endDate)
    while (cur <= end) {
      records.push({
        shopId: batchFormData.shopId!,
        spuId: batchFormData.spuId!,
        date: cur.toISOString().split('T')[0],
        totalCount: batchFormData.totalCount
      })
      cur = new Date(cur.getTime() + 86400000)
    }
    await inventoryApi.batchSet(records)
    message.success('批量设置成功')
    batchDialogVisible.value = false
    await getList()
  } finally {
    batchFormLoading.value = false
  }
}

onMounted(() => {
  fetchDropdownData()
  getList()
})
</script>
